<script setup>
import moment from 'moment'

import { getI18n } from '@/plugins/i18n'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useVModel } from '@vueuse/core'
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup'
import { menuRoutesEnum } from '@/router'

import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import InformationToolTip from '@/components/shared/InformationToolTip.vue'
import { usePortalStore } from '@/stores/portal.store'
import { garageApi } from '@/apis/garage.api'

import { useDocumentService } from '@/services/document.service'
import { formatDate, formatTime } from '@/lib/utils'

const emit = defineEmits(['update:visible', 'save', 'cancel'])
const props = defineProps({
  visible: {
    type: Boolean,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Object,
    default: null
  },
})

const popupVisible = useVModel(props, 'visible', emit)
const options = computed(() => props.options)

const editingWorkTimeEntries = ref([])
const originalWorkTimeEntries = ref([])
const editingWorkTimeEntryId = ref(null)
const errorStart = ref(null)
const errorEnd = ref(null)
const errorDay = ref(null)
const i18n = getI18n()
const portalStore = usePortalStore()

const { extractReferenceData } = useDocumentService()

const sortedWorksheets = computed(() => {
  return [...(props?.options?.worksheets || [])].sort((a, b) => {
    if (a.series !== b.series) {
      return a.series.localeCompare(b.series)
    }
    return a.number_mnemonic - b.number_mnemonic
  })
})

const sortedEditingWorkTimeEntries = computed(() => {
  return [...(editingWorkTimeEntries.value || [])].sort((a, b) => {
    return new Date(a.start) - new Date(b.start)
  })
})

const onSave = async() => {
  if (!options.value?.saveButton?.disabled) {
    await Promise.all(
      editingWorkTimeEntries.value.map(e => {
        const data = {
          start: e.start,
          end: e.end,
          is_manual: true
        }

        return garageApi.updateWorkTimeEntryAsync(e.id, data)
      })
    )

    emit('save')
  }
}

const onClose = () => {
  if (!options.value?.cancelButton?.disabled) {
    emit('cancel')
  }
}

const openTimePicker = (workTimeEntryId) => {
  editingWorkTimeEntryId.value = workTimeEntryId
}

const getTimeModel = (workTimeEntryId, start = true) => {
  return computed({
    get() {
      const wte = editingWorkTimeEntries.value.find(i => i.id === workTimeEntryId)
      if (!wte) return

      const field = start ? 'start' : 'end'
      const date = new Date(wte[field])
      if (!date) return null

      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    },
    set(value) {
      if (!value) return

      const [hours, minutes] = value.split(':')
      const wte = editingWorkTimeEntries.value.find(i => i.id === workTimeEntryId)
      if (!wte) return

      const field = start ? 'start' : 'end'
      const date = new Date(wte[field])

      date.setHours(Number(hours), Number(minutes), 0, 0)

      wte[field] = date
    }
  })
}

const isTimeAllowed = (workTimeEntryId, hour, minute, isStart = true) => {
  const original = originalWorkTimeEntries.value.find(x => x.id === workTimeEntryId)
  if (!original?.start) return true

  const editingEntry = editingWorkTimeEntries.value.find(x => x.id === workTimeEntryId)
  if (!editingEntry) return true

  const referenceStart = isStart ? new Date(original.start) : new Date(editingEntry.start)
  const referenceStartHour = referenceStart.getHours()
  const referenceStartMinute = referenceStart.getMinutes()

  const referenceEnd = original.end ? new Date(original.end) : null
  const referenceEndHour = referenceEnd ? referenceEnd.getHours() : null
  const referenceEndMinute = referenceEnd ? referenceEnd.getMinutes() : null

  if (minute === null || minute === undefined) {
    if (referenceEndHour !== null) {
      return hour >= referenceStartHour && hour <= referenceEndHour
    } else {
      return hour >= referenceStartHour
    }
  }

  if (referenceEndHour !== null && hour === referenceStartHour && hour === referenceEndHour) {
    return minute > referenceStartMinute && minute < referenceEndMinute
  } else if (hour === referenceStartHour) {
    return minute > referenceStartMinute
  } else if (referenceEndHour !== null && hour === referenceEndHour) {
    return minute < referenceEndMinute
  }

  if (!isStart) {
    const currentStart = new Date(editingEntry.start)
    const selectedTime = new Date(currentStart)
    selectedTime.setHours(hour, minute, 0, 0)
    if (selectedTime <= currentStart) {
      return false
    }
  }

  return true
}

const getChipStartDate = (start) => {
  return formatTime(moment.utc(start).local().toDate(), portalStore.selectedLanguage)
}

const getChipEndDate = (end, start) => {
  if (end) {
    return formatTime(moment.utc(end).local().toDate(), portalStore.selectedLanguage)
  }
  return formatTime(moment.utc(start).local().toDate(), portalStore.selectedLanguage).slice(0, -5) + '--:--'
}

const lastKeyPress = ref({ key: '', time: 0 })
const handleKeyDown = (e) => {
  if (e.key === 'Escape' || e.key === 'Enter') {
    e.stopImmediatePropagation()
    e.preventDefault()

    const now = Date.now()
    const timeDiff = now - lastKeyPress.value.time

    if (e.key === lastKeyPress.value.key && timeDiff < 500) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'Enter') onSave()
    }

    lastKeyPress.value = { key: e.key, time: now }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown, true)
  editingWorkTimeEntries.value = structuredClone(props?.options?.workTimeEntries || [])
  originalWorkTimeEntries.value = structuredClone(props?.options?.workTimeEntries || [])
  errorStart.value = formatTime(moment.utc(props.options.newWorkTimeEntry.start).local().toDate(), portalStore.selectedLanguage)
  errorEnd.value = props.options?.newWorkTimeEntry?.end ? formatTime(moment.utc(props.options.newWorkTimeEntry.end).local().toDate(), portalStore.selectedLanguage) : formatTime(moment.utc(new Date()).local().toDate(), portalStore.selectedLanguage)
  errorDay.value = formatDate(moment.utc(props.options.newWorkTimeEntry.start).local().toDate(), portalStore.selectedLanguage, false)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown, true)
})

</script>

<template>
  <dx-popup
    v-model:visible="popupVisible"
    :drag-enabled="true"
    :show-title="true"
    :hide-on-outside-click="false"
    :show-close-button="true"
    :close-on-esc="false"
    height="auto"
    width="auto"
    :title="options?.title"
  >
    <div class="q-pa-md q-gutter-sm">
      <i18n-t
        keypath="common.workTimeOverlap"
        tag="p"
      >
        <template #day>
          <strong>{{ errorDay }}</strong>
        </template>
        <template #start>
          <strong>{{ errorStart }}</strong>
        </template>
        <template #end>
          <strong>{{ errorEnd }}</strong>
        </template>
        <template #full_name>
          <strong>{{ props.options.mechanic.full_name }}</strong>
        </template>
      </i18n-t>
      <ul>
        <li
          v-for="ws in sortedWorksheets"
          :key="ws.id"
        >
          <reference-link
            :data="{
              series: ws.series,
              number: ws.number_mnemonic
            }"
            :text-fields="['prefix', 'series', 'number']"
            :query-fields="['series', 'number']"
            :target="menuRoutesEnum.WORKSHEETS"
          >
            <information-tool-tip :data="extractReferenceData({...ws, type: portalStore.documentTypesEnum.GARAGE_WORKSHEET })" />
          </reference-link>
          <ul>
            <li
              v-for="wte in sortedEditingWorkTimeEntries.filter(i => i.worksheet_id === ws.id)"
              :key="wte.id"
            >
              <div style="display: flex; align-items: center;">
                <q-chip
                  size="sm"
                  clickable
                  text-color="white"
                  :label="getChipStartDate(wte.start)"
                  icon-right="mdi-pencil"
                  color="grey"
                  @click="openTimePicker(wte.id)"
                >
                  <q-popup-proxy
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-time
                      v-model="getTimeModel(wte.id).value"
                      format24h
                      :options="(hr, min) => isTimeAllowed(wte.id, hr, min)"
                    />
                  </q-popup-proxy>
                </q-chip>
                -
                <q-chip
                  size="sm"
                  clickable
                  text-color="white"
                  :label="getChipEndDate(wte.end, wte.start)"
                  icon-right="mdi-pencil"
                  color="grey"
                  @click="openTimePicker(wte.id)"
                >
                  <q-popup-proxy
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-time
                      v-model="getTimeModel(wte.id, false).value"
                      format24h
                      :options="(hr, min) => isTimeAllowed(wte.id, hr, min, false)"
                    />
                  </q-popup-proxy>
                </q-chip>
              </div>
            </li>
          </ul>
        </li>
      </ul>
    </div>
    <dx-toolbar-item
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{
        text: i18n.t('common.save'),
        onClick: onSave
      }"
    />
    <dx-toolbar-item
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{
        text: i18n.t('common.close'),
        onClick: onClose
      }"
    />
  </dx-popup>
</template>

<style lang="scss">
</style>
